import { useEffect, useState } from 'react';
import { useLobby, GameAddress, Ago, SetTitle } from '../utils/Lobby';
import { SeatColor, SeatColors } from './Constants/SeatColors';
import TopBar from './TopBar';
import SignIn from './SignIn';
import NewGameDialog from './NewGameDialog';
import './Lobby.css';

// The front page: our games as cards, those waiting on us first (our turn, a decision of ours, or new: started by
// someone else and not yet opened), then those waiting on others, then the finished ones; with the development tools,
// everyone else's games too. Each says whose turn it is, where the game is, everyone's culture, and when it last moved.
// Kept up to date while it is open (see utils/Lobby.js). Not signed in, with the server's own sign-in: its sign-in page.
function GameList()
{
    const { me, games, users, signIn, error } = useLobby();
    const [newGame, setNewGame] = useState(false);

    const mine = (games ?? []).filter(game => game.mySeat >= 0);
    const finished = mine.filter(game => game.status.state === 'over');
    const needsMe = mine.filter(game => game.status.state !== 'over' && (game.you || game.isNew));
    const others = mine.filter(game => game.status.state !== 'over' && !game.you && !game.isNew);
    const notMine = (games ?? []).filter(game => game.mySeat < 0);
    // (newest activity first)
    const recent = (list) => [...list].sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '') || Number(b.id) - Number(a.id));

    useEffect(() => {
        SetTitle('Through the Ages');
    }, []);

    if (signIn)
    {
        return <div className="tta-lobby">
            <TopBar/>
            <main className="tta-lobby-main tta-lobby-narrow">
                <h1 className="tta-lobby-heading">Welcome</h1>
                <p className="tta-muted">Sign in to see your games, or sign up with the code you were given.</p>
                <div className="tta-panel"><SignIn/></div>
            </main>
        </div>;
    }

    return <div className="tta-lobby">
        <TopBar page="games"/>
        <main className="tta-lobby-main">
            {error && <div className="tta-lobby-error">{error}</div>}
            {me && <div className="tta-lobby-hero">
                <div>
                    <h1 className="tta-lobby-heading">Hello, {me.name}</h1>
                    <div className="tta-muted">
                        {games === null ? 'Loading your games…'
                            : needsMe.length > 0 ? `${needsMe.length} game${needsMe.length === 1 ? ' is' : 's are'} waiting on you.`
                            : mine.length > 0 ? 'Nothing is waiting on you right now.' : 'You are not in any games yet.'}
                    </div>
                </div>
                {me.admin ? <a className="tta-primary-button" href="./?admin">Admin page</a>
                    : <button className="tta-primary-button" onClick={() => setNewGame(true)}>+ New game</button>}
            </div>}

            {games && mine.length === 0 && notMine.length === 0 && me?.admin &&
                <div className="tta-empty">
                    <div className="tta-empty-mark">◆</div>
                    <div>Admins run the site and don't play: players, games and the sign-up code are on the admin page.</div>
                </div>}
            {games && mine.length === 0 && notMine.length === 0 && !me?.admin &&
                <div className="tta-empty">
                    <div className="tta-empty-mark">◆</div>
                    <div>Start a game, and pick who to play with. They'll find it waiting for them here.</div>
                    <button className="tta-primary-button" onClick={() => setNewGame(true)}>Start your first game</button>
                </div>}

            <Section title="Waiting on you" games={recent(needsMe)} me={me}/>
            <Section title="Waiting on others" games={recent(others)} me={me}/>
            <Section title="Finished" games={recent(finished)} me={me}/>
            <Section title="Everyone else's games" note="development" games={[...notMine].sort((a, b) => Number(a.id) - Number(b.id))} me={me}/>
        </main>
        {newGame && me && <NewGameDialog me={me} users={users} onClose={() => setNewGame(false)}/>}
    </div>;
}

function Section({ title, note, games, me })
{
    if (games.length === 0) return null;
    return <section className="tta-lobby-section">
        <h2 className="tta-lobby-section-title">{title} <span className="tta-muted">{note ?? games.length}</span></h2>
        <div className="tta-game-grid">
            {games.map(game => <GameCard key={game.id} game={game} me={me}/>)}
        </div>
    </section>;
}

// A game at a glance: its title and where it stands, who is in it (whose turn, who it waits on, their culture), and
// when it last moved
function GameCard({ game, me })
{
    const over = game.status.state === 'over';
    const badge = game.you === 'turn' ? ['Your turn', 'turn'] : game.you === 'decision' ? ['Your decision', 'turn']
        : game.isNew ? ['New', 'new'] : over ? [game.result?.winners.includes(game.players[game.mySeat]) ? 'You won' : 'Finished', 'over'] : null;
    const waitingOn = new Set(over ? [] : game.status.waitingOn.length > 0 ? game.status.waitingOn : [game.status.name]);
    // The player whose turn it is: 'turn' (choosing what their won aggression or war does too), or 'deciding' while the
    // game waits on them for an event (e.g. their bid for a territory they revealed)
    const decidingEvent = ['event', 'colonization'].includes(game.status.state) && game.status.waitingOn.includes(game.status.name);
    const currentMarker = decidingEvent ? ['deciding', 'Deciding on an event'] : ['turn', 'Their turn'];
    // Anyone else it waits on: 'defending' against an aggression, otherwise 'deciding' (e.g. a war they won)
    const otherMarker = game.status.state === 'aggression' ? ['defending', 'Deciding whether to defend'] : ['deciding', 'Waiting on them'];
    const winners = new Set(game.result?.winners ?? []);
    return <a className={`tta-game-card${game.you ? ' yours' : ''}${game.isNew ? ' new' : ''}${over ? ' over' : ''}`} href={GameAddress(game)}>
        <div className="tta-game-card-head">
            <div className="tta-game-card-title">{game.title}</div>
            {badge && <span className={`tta-badge ${badge[1]}`}>{badge[0]}</span>}
        </div>
        <div className="tta-game-card-where">
            {over ? `Finished in round ${game.status.round}` : <>{game.age} · Round {game.status.round} · {game.status.label}</>}
        </div>
        {game.isNew && game.createdBy && <div className="tta-game-card-invite">{game.createdBy} started this game with you</div>}
        {game.description && <div className="tta-muted">{game.description}</div>}
        <div className="tta-game-card-columns"><span>Players</span><span>Culture</span></div>
        <ul className="tta-game-card-players">
            {game.players.map((name, seat) =>
                <li key={seat} className={`${seat === game.mySeat ? 'me' : ''}${game.resigned[seat] ? ' resigned' : ''}`}>
                    <span className="tta-seat-dot" style={{ background: SeatColor(seat, SeatColors(game.players.length, game.mySeat, me?.color)) }}/>
                    <span className="tta-game-card-name">{name}</span>
                    {waitingOn.has(name) && <span className="tta-turn-marker" title={name === game.status.name ? currentMarker[1] : otherMarker[1]}>
                        {name === game.status.name ? currentMarker[0] : otherMarker[0]}</span>}
                    {winners.has(name) && <span className="tta-trophy" title="Won">★</span>}
                    <span className="tta-game-card-culture" title="Culture">{game.resigned[seat] ? 'resigned' : game.culture[seat]}</span>
                </li>)}
        </ul>
        {game.replayError && <div className="tta-lobby-error">{game.replayError}</div>}
        <div className="tta-game-card-foot">
            <span>{game.createdBy && !game.isNew ? `Started by ${game.createdBy === me?.name ? 'you' : game.createdBy}` : ''}</span>
            <span>{game.version > 0 ? `Last move ${Ago(game.updatedAt)}` : `Started ${Ago(game.createdAt)}`}</span>
        </div>
    </a>;
}

export default GameList;
